import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2, MailCheck, Save, Building2, User, Briefcase, FileText, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { OtpInput, maskEmail } from "@/components/OtpInput";
import { VsMark } from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const LIST_FIELDS = new Set(["services", "products", "technologies", "industries_served", "locations_served",
  "expertise", "client_references", "staffing_types", "technology_areas", "hiring_locations",
  "specializations", "experience_bands", "locations"]);

const ROLE_BUSINESS_FIELDS = {
  client: [
    { k: "primary_category", label: "Primary Business Category", req: true },
    { k: "services", label: "Services / Business Areas" },
    { k: "technologies", label: "Technologies Used" },
    { k: "procurement_contact", label: "Procurement Contact (email)" },
    { k: "staffing_contact", label: "Staffing Contact (email)" },
    { k: "finance_contact", label: "Finance Contact (email)" },
  ],
  vendor: [
    { k: "services", label: "Services", req: true },
    { k: "products", label: "Products" },
    { k: "technologies", label: "Technologies" },
    { k: "industries_served", label: "Industries Served" },
    { k: "locations_served", label: "Locations Served" },
    { k: "expertise", label: "Areas of Expertise" },
    { k: "team_size", label: "Team Size" },
    { k: "delivery_capability", label: "Delivery Capability" },
    { k: "portfolio", label: "Portfolio (links)" },
    { k: "client_references", label: "Client References" },
    { k: "rate_card", label: "Rate Card (summary)" },
  ],
  staffing: [
    { k: "staffing_types", label: "Staffing Types (IT, Non-IT, Contract, Permanent…)", req: true },
    { k: "industries_served", label: "Industries Served" },
    { k: "technology_areas", label: "Technology Areas" },
    { k: "hiring_locations", label: "Hiring Locations" },
    { k: "candidate_capacity", label: "Candidate Capacity (per month)" },
    { k: "experience_bands", label: "Experience Bands" },
    { k: "specializations", label: "Recruitment Specializations" },
  ],
};

const BUSINESS_TITLE = { client: "Business Details", vendor: "Capabilities", staffing: "Staffing Capabilities" };

function Field({ label, req, value, onChange, placeholder, type = "text", list, testid }) {
  return (
    <div>
      <Label className="text-slate-700">{label}{req && <span className="text-rose-500 ml-0.5">*</span>}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || (list ? "Comma separated" : undefined)}
        className="mt-1.5" data-testid={testid} />
    </div>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const { user, organization, refresh, verifyEmail, resendOtp, logout } = useAuth();
  const [status, setStatus] = React.useState(null);
  const [step, setStep] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const [profile, setProfile] = React.useState({ name: "", designation: "", department: "", phone: "" });
  const [company, setCompany] = React.useState({ legal_name: "", display_name: "", website: "", industry: "", size: "", description: "", hq_address: "", city: "", state: "", country: "", postal_code: "", locations: "" });
  const [business, setBusiness] = React.useState({});
  const [docs, setDocs] = React.useState({ pan: "", gst: "", msme: "", registration_number: "", certifications: "" });
  const [otp, setOtp] = React.useState("");
  const [cooldown, setCooldown] = React.useState(0);

  const load = React.useCallback(async () => {
    const { data } = await api.get("/onboarding/status");
    setStatus(data);
    if (!data.email_verified) setStep(0);
    else if (!data.complete) setStep(1);
  }, []);

  React.useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || "", designation: user.designation || "", department: user.department || "", phone: user.phone || "" });
    if (organization) {
      setCompany({
        legal_name: organization.legal_name || organization.name || "", display_name: organization.display_name || "",
        website: organization.website || "", industry: organization.industry || "", size: organization.size || "",
        description: organization.description || "", hq_address: organization.hq_address || "",
        city: organization.city || "", state: organization.state || "", country: organization.country || "",
        postal_code: organization.postal_code || "", locations: (organization.locations || []).join(", "),
      });
      const bd = organization.business_details || {};
      const b = {};
      Object.entries(bd).forEach(([k, v]) => { b[k] = Array.isArray(v) ? v.join(", ") : (v || ""); });
      setBusiness(b);
      setDocs({ pan: organization.pan || "", gst: organization.gst || "", msme: organization.msme || "", registration_number: organization.registration_number || "", certifications: (organization.certifications || []).join(", ") });
    }
    load().catch(() => {});
  }, [user?.id]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!user) return null;
  if (user.role === "super_admin") { nav("/app/overview"); return null; }

  const steps = [
    { key: "email", label: "Verification", icon: MailCheck },
    { key: "profile", label: "Profile", icon: User },
    { key: "company", label: "Company", icon: Building2 },
    { key: "business", label: BUSINESS_TITLE[user.role] || "Business", icon: Briefcase },
    { key: "documents", label: "Documents", icon: FileText },
    { key: "review", label: "Review", icon: ClipboardCheck },
  ];

  const save = async (fn, payload, advance = true) => {
    setSaving(true); setError("");
    try {
      const { data } = await fn(payload);
      setStatus(data.status);
      await refresh();
      if (advance) setStep((s) => Math.min(s + 1, steps.length - 1));
      else toast.success("Progress saved. You can continue later.");
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doVerify = async () => {
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return; }
    setSaving(true); setError("");
    try {
      await verifyEmail(user.email, otp);
      toast.success("Email verified");
      await load();
      setStep(1);
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doResend = async () => {
    setError("");
    try { await resendOtp(user.email); setCooldown(60); toast.success("A new code was sent"); }
    catch (err) { setError(apiError(err.response?.data?.detail)); }
  };

  const submitAll = async () => {
    setSaving(true); setError("");
    try {
      await api.post("/onboarding/submit");
      await refresh();
      toast.success("Profile submitted for verification");
      nav("/app/overview");
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveCompany = (advance) => save(api.put.bind(api, "/onboarding/company"), {
    ...company, locations: company.locations ? company.locations.split(",").map((s) => s.trim()).filter(Boolean) : [],
  }, advance);

  const saveBusiness = (advance) => {
    const payload = {};
    Object.entries(business).forEach(([k, v]) => {
      payload[k] = LIST_FIELDS.has(k) ? (v ? v.split(",").map((s) => s.trim()).filter(Boolean) : []) : v;
    });
    return save(api.put.bind(api, "/onboarding/business"), { details: payload }, advance);
  };

  const saveDocs = (advance) => save(api.put.bind(api, "/onboarding/documents"), {
    ...docs, certifications: docs.certifications ? docs.certifications.split(",").map((s) => s.trim()).filter(Boolean) : [],
  }, advance);

  const sec = (key) => status?.sections?.find((s) => s.key === key);
  const percent = status?.percent ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <VsMark />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-vs-ink">VS PartnerHub</div>
              <div className="text-[10px] text-slate-500">Organization Onboarding</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs font-semibold text-slate-600" data-testid="onboarding-progress-label">Profile Completion: {percent}%</div>
            <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-vs-royal transition-all" style={{ width: `${percent}%` }} /></div>
            <button onClick={async () => { await logout(); nav("/login"); }} className="text-xs text-slate-400 hover:text-slate-600" data-testid="onboarding-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-display font-bold text-2xl text-vs-ink">Complete Your Organization Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Complete your profile to activate your VS PartnerHub workspace.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-6" data-testid="onboarding-stepper">
          {steps.map((s, i) => {
            const done = (s.key === "email" && status?.email_verified) || (sec(s.key)?.complete);
            return (
              <React.Fragment key={s.key}>
                <button onClick={() => (status?.email_verified || i === 0) && setStep(i)}
                  className={`shrink-0 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${step === i ? "border-vs-royal bg-vs-light text-vs-royal" : done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}
                  data-testid={`onboarding-step-${s.key}`}>
                  {done ? <Check className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}{s.label}
                </button>
                {i < steps.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="vs-card p-6 sm:p-8">
          {error && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="onboarding-error">{error}</div>}

          {step === 0 && (
            <div className="text-center max-w-sm mx-auto">
              <div className="h-12 w-12 rounded-xl bg-vs-light flex items-center justify-center mx-auto mb-4"><MailCheck className="h-6 w-6 text-vs-royal" /></div>
              <h2 className="font-display font-bold text-xl text-vs-ink">Verify your email</h2>
              <p className="text-sm text-slate-500 mt-2">We've sent a 6-digit verification code to<br /><span className="font-semibold text-slate-700">{maskEmail(user.email)}</span></p>
              <div className="mt-6"><OtpInput value={otp} onChange={setOtp} disabled={saving} /></div>
              <Button onClick={doVerify} disabled={saving || otp.length !== 6} className="w-full mt-6 bg-vs-royal hover:bg-vs-blue text-white" data-testid="onboarding-verify-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify Email"}
              </Button>
              <div className="mt-4 text-sm text-slate-500">
                Didn't receive the code?{" "}
                <button onClick={doResend} disabled={cooldown > 0} className="text-vs-royal font-medium disabled:text-slate-400" data-testid="onboarding-resend-btn">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-display font-bold text-lg text-vs-ink mb-1">Personal Profile</h2>
              <p className="text-sm text-slate-500 mb-5">Your details as the account owner. Work phone is optional and is never used for sign-in.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" req value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} testid="ob-name" />
                <Field label="Designation" req value={profile.designation} onChange={(v) => setProfile({ ...profile, designation: v })} placeholder="e.g. Head of Procurement" testid="ob-designation" />
                <Field label="Department" value={profile.department} onChange={(v) => setProfile({ ...profile, department: v })} testid="ob-department" />
                <Field label="Work Phone (optional)" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} testid="ob-phone" />
              </div>
              <StepActions saving={saving} onLater={() => save(api.put.bind(api, "/onboarding/profile"), profile, false)} onNext={() => save(api.put.bind(api, "/onboarding/profile"), profile)} nextTestid="ob-profile-next" />
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display font-bold text-lg text-vs-ink mb-1">Company Information</h2>
              <p className="text-sm text-slate-500 mb-5">Legal and operational details of your organization.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company Legal Name" req value={company.legal_name} onChange={(v) => setCompany({ ...company, legal_name: v })} testid="ob-legal-name" />
                <Field label="Display Name" value={company.display_name} onChange={(v) => setCompany({ ...company, display_name: v })} testid="ob-display-name" />
                <Field label="Company Website" value={company.website} onChange={(v) => setCompany({ ...company, website: v })} placeholder="https://" testid="ob-website" />
                <Field label="Industry" req value={company.industry} onChange={(v) => setCompany({ ...company, industry: v })} testid="ob-industry" />
                <Field label="Company Size" value={company.size} onChange={(v) => setCompany({ ...company, size: v })} placeholder="e.g. 51-200" testid="ob-size" />
                <Field label="Headquarters Address" value={company.hq_address} onChange={(v) => setCompany({ ...company, hq_address: v })} testid="ob-hq" />
                <Field label="City" value={company.city} onChange={(v) => setCompany({ ...company, city: v })} testid="ob-city" />
                <Field label="State" value={company.state} onChange={(v) => setCompany({ ...company, state: v })} testid="ob-state" />
                <Field label="Country" req value={company.country} onChange={(v) => setCompany({ ...company, country: v })} testid="ob-country" />
                <Field label="Postal Code" value={company.postal_code} onChange={(v) => setCompany({ ...company, postal_code: v })} testid="ob-postal" />
                <div className="sm:col-span-2"><Field label="Business Locations" list value={company.locations} onChange={(v) => setCompany({ ...company, locations: v })} testid="ob-locations" /></div>
                <div className="sm:col-span-2"><Field label="Company Description" value={company.description} onChange={(v) => setCompany({ ...company, description: v })} testid="ob-description" /></div>
              </div>
              <StepActions saving={saving} onLater={() => saveCompany(false)} onNext={() => saveCompany(true)} onBack={() => setStep(1)} nextTestid="ob-company-next" />
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display font-bold text-lg text-vs-ink mb-1">{BUSINESS_TITLE[user.role]}</h2>
              <p className="text-sm text-slate-500 mb-5">Tell partners what your organization does and how it operates.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {(ROLE_BUSINESS_FIELDS[user.role] || []).map((f) => (
                  <Field key={f.k} label={f.label} req={f.req} list={LIST_FIELDS.has(f.k)} value={business[f.k] || ""}
                    onChange={(v) => setBusiness({ ...business, [f.k]: v })} testid={`ob-biz-${f.k}`} />
                ))}
              </div>
              <StepActions saving={saving} onLater={() => saveBusiness(false)} onNext={() => saveBusiness(true)} onBack={() => setStep(2)} nextTestid="ob-business-next" />
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display font-bold text-lg text-vs-ink mb-1">Business / KYC Details</h2>
              <p className="text-sm text-slate-500 mb-5">Provide at least one registration identifier. Supporting file uploads will be enabled in your Document Center.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="PAN" value={docs.pan} onChange={(v) => setDocs({ ...docs, pan: v })} testid="ob-pan" />
                <Field label="GST" value={docs.gst} onChange={(v) => setDocs({ ...docs, gst: v })} testid="ob-gst" />
                <Field label="MSME" value={docs.msme} onChange={(v) => setDocs({ ...docs, msme: v })} testid="ob-msme" />
                <Field label="Company Registration No." value={docs.registration_number} onChange={(v) => setDocs({ ...docs, registration_number: v })} testid="ob-regno" />
                <div className="sm:col-span-2"><Field label="Certifications" list value={docs.certifications} onChange={(v) => setDocs({ ...docs, certifications: v })} testid="ob-certs" /></div>
              </div>
              <StepActions saving={saving} onLater={() => saveDocs(false)} onNext={() => saveDocs(true)} onBack={() => setStep(3)} nextTestid="ob-docs-next" />
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-display font-bold text-lg text-vs-ink mb-5">Review & Submit</h2>
              <div className="space-y-2.5">
                {status?.sections?.map((s) => (
                  <div key={s.key} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`review-section-${s.key}`}>
                    <span className="text-sm font-medium text-slate-700">{s.label}</span>
                    {s.complete
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" />Complete</span>
                      : <span className="text-xs font-semibold text-amber-600">Incomplete</span>}
                  </div>
                ))}
              </div>
              {status?.complete ? (
                <p className="text-sm text-slate-500 mt-4">Everything is in place. Submit your profile for VS-Solutions verification to activate your workspace.</p>
              ) : (
                <p className="text-sm text-amber-600 mt-4">Complete all required sections above before submitting.</p>
              )}
              <div className="flex items-center justify-between mt-6">
                <Button variant="ghost" onClick={() => setStep(4)} data-testid="ob-review-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
                <Button onClick={submitAll} disabled={saving || !status?.complete} className="bg-vs-royal hover:bg-vs-blue text-white" data-testid="ob-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit for Verification<ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

function StepActions({ saving, onNext, onLater, onBack, nextTestid }) {
  return (
    <div className="flex items-center justify-between mt-6">
      <div>{onBack && <Button variant="ghost" onClick={onBack} data-testid="ob-back-btn"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>}</div>
      <div className="flex items-center gap-2.5">
        <Button variant="outline" onClick={onLater} disabled={saving} data-testid="ob-save-later-btn"><Save className="h-4 w-4 mr-1.5" />Save & Continue Later</Button>
        <Button onClick={onNext} disabled={saving} className="bg-vs-royal hover:bg-vs-blue text-white" data-testid={nextTestid}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save & Continue<ArrowRight className="ml-1.5 h-4 w-4" /></>}
        </Button>
      </div>
    </div>
  );
}
